/**
 * Thin wrapper around fetch that:
 *   - Reads `NEXT_PUBLIC_API_BASE_URL` once.
 *   - Attaches the JWT from cookie/sessionStorage (via utils/auth).
 *   - Throws on non-OK responses with the server's `error.message` if
 *     present, so callers can surface meaningful toasts.
 *
 * Existing pages using inline `fetch` keep working — this is opt-in.
 * Adopt it for new pages so the boilerplate isn't reinvented.
 */
import { getAuthToken, isViewOnly } from '@/utils/auth';

export { getAuthToken };
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

const VIEW_ONLY_MESSAGE =
  'Your account is inactive, so this is a view-only session. You can browse but can’t make changes. Please contact your school administrator.';

// Short-circuit any mutating call for a read-only session so the user
// gets a friendly toast instead of a round-trip 403. The server blocks
// these anyway (defence in depth); this is purely UX.
const assertCanWrite = () => {
  if (isViewOnly()) {
    const err = new Error(VIEW_ONLY_MESSAGE);
    err.status = 403;
    err.code = 'VIEW_ONLY';
    throw err;
  }
};

/**
 * The server ends the session when the account's status or role changes,
 * so the token can never quietly describe access the user no longer has.
 *
 * Sign them out here rather than surfacing the 401 as a page error: the
 * stored token is now worthless, and every subsequent request would fail
 * the same way. Signing back in mints a token with the new access already
 * in it — which is the whole point of revoking rather than patching.
 *
 * Guarded so it only fires once: a screen with six panels loading at once
 * gets six 401s, and six redirects would fight each other.
 */
let signingOut = false;
const honourSessionRevoked = async (response) => {
  if (response?.status !== 401 || signingOut) return;
  let payload = null;
  try {
    payload = await response.clone().json();
  } catch {
    return; // not our JSON error shape — leave it to the caller
  }
  const code = payload?.error?.code || payload?.code;
  if (code !== 'SESSION_REVOKED') return;

  signingOut = true;
  const message =
    payload?.error?.message ||
    payload?.message ||
    'Your session has expired. Please sign in again.';

  // Stashed under a key `logout` does not clear (it removes named keys,
  // not the whole store), so the login form can tell the user why they
  // landed there instead of dumping them at a blank form.
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem('kaympus:signOutReason', message);
    } catch {
      /* private mode — the form just shows its default copy */
    }
  }

  const { logout } = await import('@/utils/auth');
  // No router here, so `logout` falls back to a location.replace — which is
  // what we want anyway: a full reload drops every cached permission and
  // in-memory store belonging to the old session.
  logout(null, null);
};

const buildHeaders = (extra = {}, hasJsonBody = true) => {
  const headers = { ...extra };
  if (hasJsonBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

// A message that is really a web page — an upstream host's HTML error body
// (Cloudflare's 525 page, a proxy's 502) that leaked through a server
// message. It must never be printed in a toast (BUG-058). The server now
// strips these itself; this is the safety net for every other path.
const LOOKS_LIKE_HTML = /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<\/[a-z]+>/i;
const GENERIC_FAILURE = 'Something went wrong on the server. Please try again in a moment.';

export const sanitiseErrorMessage = (message, status) => {
  const text = String(message || '').trim();
  if (!text) return `Request failed (${status})`;
  if (LOOKS_LIKE_HTML.test(text) || text.length > 600) return GENERIC_FAILURE;
  return text;
};

const parseError = async (response) => {
  let message = `Request failed (${response.status})`;
  let code = null;
  let details = null;
  try {
    const data = await response.json();
    if (data?.error?.message) message = data.error.message;
    else if (data?.message) message = data.message;
    code = data?.error?.code || data?.code || null;
    details = data?.error && typeof data.error === 'object' ? data.error : null;
  } catch {
    // ignore — non-JSON error body
  }
  const err = new Error(sanitiseErrorMessage(message, response.status));
  err.status = response.status;
  // Callers branch on this (VIEW_ONLY, SESSION_REVOKED, CLASS_INACTIVE…);
  // dropping it forced them to string-match the message.
  if (code) err.code = code;
  // The structured fields the server put next to the code (seat counts,
  // the field a setting error is about, the routes a bus still runs…).
  if (details) err.details = details;
  return err;
};

export const apiGet = async (path, { signal } = {}) => {
  if (!API_BASE_URL) throw new Error('NEXT_PUBLIC_API_BASE_URL is not configured.');
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: buildHeaders({}, false),
    signal,
  });
  if (!response.ok) {
    await honourSessionRevoked(response);
    throw await parseError(response);
  }
  return response.json();
};

export const apiPost = async (path, body, { signal } = {}) => {
  if (!API_BASE_URL) throw new Error('NEXT_PUBLIC_API_BASE_URL is not configured.');
  assertCanWrite();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!response.ok) {
    await honourSessionRevoked(response);
    throw await parseError(response);
  }
  return response.json();
};

export const apiPut = async (path, body, { signal } = {}) => {
  if (!API_BASE_URL) throw new Error('NEXT_PUBLIC_API_BASE_URL is not configured.');
  assertCanWrite();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!response.ok) {
    await honourSessionRevoked(response);
    throw await parseError(response);
  }
  return response.json();
};

export const apiPatch = async (path, body, { signal } = {}) => {
  if (!API_BASE_URL) throw new Error('NEXT_PUBLIC_API_BASE_URL is not configured.');
  assertCanWrite();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PATCH',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!response.ok) {
    await honourSessionRevoked(response);
    throw await parseError(response);
  }
  return response.json();
};

export const apiDelete = async (path, { signal } = {}) => {
  if (!API_BASE_URL) throw new Error('NEXT_PUBLIC_API_BASE_URL is not configured.');
  assertCanWrite();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: buildHeaders({}, false),
    signal,
  });
  if (!response.ok) {
    await honourSessionRevoked(response);
    throw await parseError(response);
  }
  return response.json();
};
